import React from 'react';
import { DollarSign, Calendar, BarChart2, TrendingUp, Clock, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости трансформации
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматирование срока окупаемости
  const formatPaybackPeriod = (months) => {
    if (!isFinite(months) || months <= 0) {
      return 'Не окупается';
    }
    
    const years = Math.floor(months / 12);
    const remainingMonths = Math.round(months % 12);
    
    if (years === 0) {
      return `${remainingMonths} мес.`;
    } else if (remainingMonths === 0) {
      return `${years} ${pluralize(years, 'год', 'года', 'лет')}`;
    } else {
      return `${years} ${pluralize(years, 'год', 'года', 'лет')} ${remainingMonths} мес.`;
    }
  };

  return (
    <ToolResult title="Результаты расчета стоимости трансформации">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Общая стоимость трансформации
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatCurrency(results.totalCost)}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Включает все расходы на весь период проекта{results.costByCategory.contingency > 0 ? ', включая резерв на непредвиденные расходы' : ''}
          </p>
        </div>

        {/* ROI и окупаемость */}
        {results.roi && results.roi.roiPercent !== undefined && (
          <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
            <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4"/>
              ROI и окупаемость
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
                {formatNumber(results.roi.roiPercent, 1)}%
              </span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                (за 3 года)
              </span>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              Срок окупаемости: {formatPaybackPeriod(results.roi.paybackPeriodMonths)}
            </p>
          </div>
        )}
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Базовая стоимость проекта" 
          value={formatCurrency(results.baseCost)} 
          color="blue" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        {results.costByCategory.contingency > 0 && (
          <ResultItem 
            label="Резерв на непредвиденные расходы" 
            value={formatCurrency(results.costByCategory.contingency)} 
            color="orange" 
            icon={<AlertCircle className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Продолжительность проекта" 
          value={`${results.projectParameters.projectDurationMonths} ${pluralize(results.projectParameters.projectDurationMonths, 'месяц', 'месяца', 'месяцев')}`} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        {results.roi && results.roi.annualBenefit > 0 && (
          <ResultItem 
            label="Ожидаемая годовая выгода" 
            value={formatCurrency(results.roi.annualBenefit)} 
            color="green"
            icon={<BarChart2 className="w-4 h-4"/>} 
          />
        )}

        {/* Разбивка по категориям */}
        <div className="pt-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Разбивка по категориям затрат:</h4>
          <div className="space-y-2">
            {Object.entries(results.costByCategory)
              .filter(([_, value]) => value > 0)
              .sort(([_, a], [__, b]) => b - a) // Сортировка по убыванию стоимости
              .map(([key, value]) => {
                const categoryLabels = {
                  internalTeam: 'Внутренняя команда',
                  consulting: 'Консалтинговые услуги',
                  software: 'Программное обеспечение',
                  hardware: 'Оборудование и инфраструктура',
                  training: 'Обучение персонала',
                  contingency: 'Резерв на непредвиденные расходы'
                };
                
                const percentage = (value / results.totalCost) * 100;
                
                return (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{categoryLabels[key] || key}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white mr-2">{formatCurrency(value)}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({formatNumber(percentage, 1)}%)</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Информация о факторах трансформации */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Примененные факторы расчета</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Отрасль: <span className="font-medium">{formatNumber(results.projectParameters.industryFactor, 2)}x</span>, 
                Размер компании: <span className="font-medium">{formatNumber(results.projectParameters.companySizeFactor, 2)}x</span>, 
                Тип трансформации: <span className="font-medium">{formatNumber(results.projectParameters.transformationTypeFactor, 2)}x</span>
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Масштаб изменений: <span className="font-medium">{formatNumber(results.projectParameters.changeSeverityFactor, 2)}x</span>, 
                Готовность организации: <span className="font-medium">{formatNumber(results.projectParameters.organizationalReadinessFactor, 2)}x</span>
              </p>
            </div>
          </div>
        </div>
        
        {/* Сроки окупаемости */}
        {results.roi && results.roi.paybackPeriodMonths !== undefined && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mt-2 border border-green-200 dark:border-green-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <Clock className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Оценка окупаемости</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Срок окупаемости = <span className="font-medium text-green-600 dark:text-green-400">{formatPaybackPeriod(results.roi.paybackPeriodMonths)}</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {results.roi.paybackPeriodMonths <= 12 ? 
                    'Очень быстрая окупаемость (менее года)' : 
                    results.roi.paybackPeriodMonths <= 24 ? 
                    'Хорошая окупаемость (1-2 года)' : 
                    results.roi.paybackPeriodMonths <= 36 ? 
                    'Средняя окупаемость (2-3 года)' : 
                    results.roi.paybackPeriodMonths <= 60 ? 
                    'Долгосрочная окупаемость (3-5 лет)' : 
                    'Очень долгая окупаемость (более 5 лет)'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слов в зависимости от числа
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default ResultsOverview;